import React, { ComponentProps, useCallback, useMemo } from 'react'
import { Keyboard, Platform, StyleProp, StyleSheet, ViewStyle } from 'react-native'
import { BottomSheetHandleProps, useBottomSheet, TouchableOpacity } from '@gorhom/bottom-sheet'
import Animated, {
  Extrapolation,
  FadeInDown,
  FadeInUp,
  interpolate,
  useAnimatedStyle,
  useDerivedValue
} from 'react-native-reanimated'
import { toRad } from 'react-native-redash'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Svg, { Path } from 'react-native-svg'
import { StatusBar } from 'expo-status-bar'

// @ts-ignore
export const transformOrigin = ({ x, y }, ...transformations) => {
  'worklet'
  return [{ translateX: x }, { translateY: y }, ...transformations, { translateX: x * -1 }, { translateY: y * -1 }]
}

interface HandleProps extends BottomSheetHandleProps {
  style?: StyleProp<ViewStyle>
}

export const ChatHandle: React.FC<HandleProps> = (props) => {
  const { style, animatedIndex } = props
  //#region animations
  const indicatorTransformOriginY = useDerivedValue(() =>
    interpolate(animatedIndex.value, [0, 1, 2], [-1, 0, 1], Extrapolation.CLAMP)
  )
  //#endregion

  //#region styles
  const containerStyle = useMemo(() => [styles.header, style], [style])
  const containerAnimatedStyle = useAnimatedStyle(() => {
    const borderTopRadius = interpolate(animatedIndex.value, [1, 2], [20, 0], Extrapolation.CLAMP)
    return {
      borderTopLeftRadius: borderTopRadius,
      borderTopRightRadius: borderTopRadius
    }
  })
  const leftIndicatorStyle = useMemo(
    () => ({
      ...styles.indicator,
      ...styles.leftIndicator
    }),
    []
  )
  const leftIndicatorAnimatedStyle = useAnimatedStyle(() => {
    const leftIndicatorRotate = interpolate(
      animatedIndex.value,
      [0, 1, 2],
      [toRad(-40), 0, toRad(40)],
      Extrapolation.CLAMP
    )
    return {
      transform: transformOrigin(
        { x: 0, y: indicatorTransformOriginY.value },
        {
          rotate: `${leftIndicatorRotate}rad`
        },
        {
          translateX: -7
        }
      )
    }
  })
  const rightIndicatorStyle = useMemo(
    () => ({
      ...styles.indicator,
      ...styles.rightIndicator
    }),
    []
  )
  const rightIndicatorAnimatedStyle = useAnimatedStyle(() => {
    const rightIndicatorRotate = interpolate(
      animatedIndex.value,
      [0, 1, 2],
      [toRad(40), 0, toRad(-40)],
      Extrapolation.CLAMP
    )
    return {
      transform: transformOrigin(
        { x: 0, y: indicatorTransformOriginY.value },
        {
          rotate: `${rightIndicatorRotate}rad`
        },
        {
          translateX: 7
        }
      )
    }
  })
  //#endregion

  // render
  return (
    <>
      <HandleHeader {...props} />
      <Animated.View style={[containerStyle, containerAnimatedStyle]} renderToHardwareTextureAndroid={true}>
        <Animated.View style={[leftIndicatorStyle, leftIndicatorAnimatedStyle]} />
        <Animated.View style={[rightIndicatorStyle, rightIndicatorAnimatedStyle]} />
      </Animated.View>
    </>
  )
}

const styles = StyleSheet.create({
  header: {
    alignContent: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'white',
    paddingVertical: 10
  },
  indicator: {
    position: 'absolute',
    width: 15,
    height: 4,
    backgroundColor: '#c4c2bb'
  },
  leftIndicator: {
    borderTopStartRadius: 2,
    borderBottomStartRadius: 2
  },
  rightIndicator: {
    borderTopEndRadius: 2,
    borderBottomEndRadius: 2
  }
})

const HandleHeader = ({ animatedIndex }: HandleProps) => {
  const insets = useSafeAreaInsets()
  const { collapse } = useBottomSheet()

  const onPress = useCallback(() => {
    Keyboard.dismiss()
    collapse()
  }, [collapse])

  const containerAnimatedStyle = useAnimatedStyle(() => {
    const isVisible = animatedIndex.value === 1
    return {
      display: isVisible ? 'flex' : 'none'
    }
  })

  return (
    <>
      {Platform.OS === 'ios' && <StatusBar style="light" hidden={animatedIndex.value === 1} />}
      <Animated.View
        entering={FadeInDown.duration(150)}
        exiting={FadeInUp.duration(150)}
        style={[
          {
            position: 'absolute',
            backgroundColor: '#F6F2E9CC',
            top: 0,
            right: 0,
            left: 0,
            width: '100%',
            paddingTop: insets.top,
            paddingHorizontal: 24,
            zIndex: 22
          },
          containerAnimatedStyle
        ]}
      >
        <TouchableOpacity
          onPress={onPress}
          style={{
            zIndex: 2,
            backgroundColor: '#fff',
            marginBottom: 12,
            width: 40,
            height: 40,
            borderRadius: 40,
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <ArrowIcon />
        </TouchableOpacity>
      </Animated.View>
    </>
  )
}

const ArrowIcon = (props: ComponentProps<typeof Svg>) => (
  <Svg width={24} height={24} viewBox="0 0 24 24" fill="none" {...props}>
    <Path
      d="M11.4697 21.0156C11.7626 21.3085 12.2374 21.3085 12.5303 21.0156C12.8232 20.7227 12.8232 20.2478 12.5303 19.9549L5.32538 12.75L20.4853 12.75C20.8995 12.75 21.2353 12.4142 21.2353 12C21.2353 11.5857 20.8995 11.25 20.4853 11.25L5.32538 11.25L12.5303 4.04501C12.8232 3.75211 12.8232 3.27724 12.5303 2.98435C12.2374 2.69145 11.7626 2.69145 11.4697 2.98435L2.45406 12L11.4697 21.0156Z"
      fill="#121212"
    />
  </Svg>
)
